import { Card, Grid, Pagination, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import Select from "../../../../components/Select/Select";
import {
  addDirectBilling,
  getClinicStocks,
  getPetBillingById,
  getServices,
  updateMultiplePayment,
} from "../../../../redux/reducers/clinicSlice";
import { getCompliantSummaryByIdWoLoader } from "../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../util/AppColors";
import {
  billingFilters,
  PaymentTypeList,
  RMVPaymentTypeList,
} from "../../../../util/dropList";
import {
  getBorderColor,
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getRightPackageUom,
  getRightSaluation,
  getSelectedInvoicesBalanceDue,
  getTaxAndSubTotalAndTotal,
  getTaxAndSubTotalAndTotalForTreatment,
  getTreatmentRecordBasedType,
  reorderList,
  roundWithSignedDiff,
} from "../../../../util/function";
import { RMVClinicId } from "../../../../util/server";
import PaymentModeDropdown from "../../../CommonComponents/PaymentModeDropdown";
import Billing from "./Billing";

const initialValues = {
  amount: "",
  paymentMode: null,
};

const BillingHistory = ({
  isEditBill,
  setEditBill,
  petId,
  selectedBill,
  setSelectedBill,
  apntId,
}) => {
  const dispatch = useDispatch();
  const contentToPrint = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [invModVisible, setInvModVisible] = useState(false);
  const [payValues, setPayValues] = useState(initialValues);
  const [payErr, setPayErr] = useState({});
  const [modalData, setModalData] = useState();
  const [payModalVisible, setPayModalVisible] = useState(false);
  const [filterType, setFilterType] = useState(billingFilters?.[0]);
  const [secFilter, setSecFilter] = useState("All");
  const [enabledPaymentTypes, setEnabledPaymentTypes] =
    useState(PaymentTypeList);
  const [totalBalanceDue, setTotalBalanceDue] = useState(0);
  const [billingList, setBillingList] = useState([]);
  const [refereneValues, setRefereneValues] = useState({
    visible: false,
    referenceNo: null,
  });
  const petBilling = useSelector((state) => state?.clinic?.petBilling);
  const clinicDet = useSelector((state) => state?.clinic?.details);
  const stocks = useSelector((state) => state?.clinic?.clinicStocks);
  const services = useSelector((state) => state?.clinic?.services);
  const petData = useSelector(
    (state) => state?.pet?.complaintSummary?.data?.pet,
  );
  const lastSummary = useSelector((state) => state?.pet?.lastSummary?.data);
  const petDetails = useSelector((state) => state?.pet?.petDetails);
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const itemsPerLoad = 10;
  const defaultUrl = `?page=1&itemSize=${itemsPerLoad}`;
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const petDet = apntId ? petData : petDetails;
  const statusActive =
    typeof petDet?.status === "boolean"
      ? petDet?.status
      : petDet?.status === "Y";

  useEffect(() => {
    if (!isEditBill) {
      dispatch(getPetBillingById({ petId, url: defaultUrl }));
      setPage(1);
      dispatch(getClinicStocks());
      dispatch(getServices());
    }
  }, [isEditBill]);

  useEffect(() => {
    dispatch(
      getPetBillingById({
        petId,
        url: `?page=1&itemSize=${itemsPerLoad}`,
      }),
    );
    setPage(1);
  }, [filterType]);

  useEffect(() => {
    let reqList = [];
    if (!searchValue) {
      reqList = (petBilling?.data ?? [])?.map((li, i) => ({
        ...li,
        i,
        isSelected: false,
      }));
    } else {
      reqList = (petBilling?.data ?? [])
        ?.filter((payment) => {
          const petNameMatch = payment?.petName
            ?.toLowerCase()
            .includes(searchValue?.toLowerCase());
          const vetNameMatch = payment?.doctorName
            ?.toLowerCase()
            .includes(searchValue?.toLowerCase());
          const paymentStatusMatch = payment?.status
            ?.toLowerCase()
            .includes(searchValue?.toLowerCase());
          const issueMatch = payment?.issue
            ?.toLowerCase()
            .includes(searchValue?.toLowerCase());
          return searchValue?.length > 0
            ? petNameMatch || vetNameMatch || paymentStatusMatch || issueMatch
            : true;
        })
        ?.map((li, i) => ({
          ...li,
          i,
          isSelected: false,
        }));
    }
    setBillingList(reqList);
  }, [petBilling?.data, searchValue]);

  const handleChangePage = (e, selectedPage) => {
    dispatch(
      getPetBillingById({
        petId,
        url: `?page=${selectedPage}&itemSize=${itemsPerLoad}`,
      }),
    );
    setPage(selectedPage);
  };

  const handleInvModClose = () => {
    setSelectedBill(null);
    setInvModVisible(false);
  };

  const handlePrint = useReactToPrint({
    documentTitle: `${clinicDet?.name ?? ""}-${
      selectedBill?.invoiceNumbers ?? ""
    }`,
    removeAfterPrint: true,
  });

  const handleChangeValues = (name, value) => {
    const reqValue = typeof value === "object" ? value?.value : value;
    const reqObj = { ...payValues, [name]: value };
    setPayValues(reqObj);
    const errObj = {
      ...payErr,
      [name]: reqValue?.length > 0 ? "" : "This field is required",
    };
    if (name === "paymentMode") {
      if (reqValue !== "advanceAdjustment") {
        errObj.amount = "";
      }
      if (reqValue === "advanceAdjustment") {
        if (Number(billingList?.[0]?.advanceAmount ?? 0) <= 0) {
          errObj.amount = "Insufficient credit balance";
        }
      }
    }
    if (name === "amount") {
      if (payValues?.paymentMode?.value === "advanceAdjustment") {
        errObj.amount =
          Number(value) < 0
            ? "Amount must be positive"
            : Number(value) > Number(billingList?.[0]?.advanceAmount ?? 0)
              ? "Insufficient credit balance"
              : "";
      } else {
        errObj.amount = Number(value) < 0 ? "Amount must be positive" : "";
      }
      if (Number(value) > totalBalanceDue) {
        errObj.amount = "Amount must be less than or equal to balance due";
      }
    }
    setPayErr(errObj);
  };

  const payModalClose = (li) => {
    setModalData(li);
    setPayValues(initialValues);
    setPayModalVisible(false);
    setPayErr({});
  };

  const getBatch = (stkId = null, batchId = null) => {
    if (!stkId || !batchId) return null;
    const reqStock = stocks?.data?.find((stc) => stc?.stockId === stkId);
    return (
      reqStock?.stockBatch?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  const finalPayment = () => {
    //validation start
    let errs = {};
    if (
      !payValues?.amount ||
      isNaN(Number(payValues?.amount)) ||
      Number(payValues?.amount) <= 0 ||
      Number(payValues?.amount) > totalBalanceDue ||
      (payValues?.paymentMode?.value === "advanceAdjustment" &&
        Number(payValues?.amount) >
          Number(billingList?.[0]?.advanceAmount ?? 0))
    ) {
      errs = {
        ...errs,
        amount:
          payValues?.paymentMode?.value === "advanceAdjustment" &&
          Number(payValues?.amount) >
            Number(billingList?.[0]?.advanceAmount ?? 0)
            ? "Insufficient credit balance"
            : !payValues?.amount
              ? "This field is required"
              : Number(payValues?.amount) > totalBalanceDue
                ? "Amount must be less than or equal to balance due"
                : Number(payValues?.amount) <= 0
                  ? "Amount must be positive"
                  : "",
      };
    }
    if (!payValues?.paymentMode?.value) {
      errs = { ...errs, paymentMode: "This field is required" };
    }
    if (Object.values(errs).some((item) => item?.length > 0)) {
      return setPayErr(errs);
    }
    if (billingList?.filter((bil) => bil?.isSelected)?.length === 0) return;
    //validation end here
    const multiplePayments = billingList
      ?.filter((bil) => bil?.isSelected)
      ?.map((bil) => ({ paymentId: bil?.paymentId }))
      ?.sort((a, b) => a?.paymentId - b?.paymentId);
    dispatch(
      updateMultiplePayment({
        multiplePayments,
        amount: Number(payValues?.amount),
        mode: payValues?.paymentMode?.value,
      }),
    ).then((res) => {
      if (res?.payload) {
        dispatch(getPetBillingById({ petId, url: defaultUrl }));
        setPage(1);
        setPayModalVisible(false);
        setPayValues(initialValues);
        setTotalBalanceDue(0);
        setPayErr({});
      }
    });
  };

  const handleCheckItem = (value, ind) => {
    const reqList = billingList?.map((bill, i) => {
      if (i === ind) return { ...bill, isSelected: value };
      return bill;
    });
    setBillingList(reqList);
  };

  const handlePayDownload = () => {
    if (billingList?.some((bl) => bl?.isSelected)) {
      const dueAmount = getSelectedInvoicesBalanceDue(
        billingList?.filter((bil) => bil?.isSelected),
        Number(0),
        services?.data,
        selectedBill?.procedure ?? [],
      );
      setPayValues({
        ...payValues,
        amount: dueAmount,
      });
      setTotalBalanceDue(dueAmount);
      if (
        billingList?.[0]?.advanceAmount &&
        Number(billingList?.[0]?.advanceAmount) <= 0
      ) {
        const filtered = PaymentTypeList.filter(
          (list) => list?.value !== "advanceAdjustment",
        );
        setEnabledPaymentTypes(filtered);
      } else {
        setEnabledPaymentTypes(PaymentTypeList);
      }
      setPayModalVisible(true);
    }
  };

  const getReferenceNos = () => {
    const list = [];
    billingList?.forEach((bl) => {
      if (bl?.invoiceNumbers?.length > 0) {
        list.push({
          name: bl?.invoiceNumbers,
          value: bl?.invoiceNumbers,
        });
      }
      if (bl?.treatmentInvoiceNumber?.length > 0) {
        list.push({
          name: bl?.treatmentInvoiceNumber,
          value: bl?.treatmentInvoiceNumber,
        });
      }
      if (bl?.petShopInvoiceNumber?.length > 0) {
        list.push({
          name: bl?.petShopInvoiceNumber,
          value: bl?.petShopInvoiceNumber,
        });
      }
    });
    return list;
  };

  const handleCreateBill = (isCreate = false) => {
    if (!isCreate && billingList?.length > 0 && getReferenceNos()?.length > 0) {
      setRefereneValues({ visible: true, referenceNo: null });
      return;
    }
    const data = {
      userId: petDet?.userId,
      type: "Clinic",
      paymentOption: "",
      direct: [],
      discountValue: 0,
      discountType: "percentage",
      petId: petDet?.petId,
      referenceNo: refereneValues?.referenceNo?.value ?? "",
    };
    dispatch(addDirectBilling(data)).then((res) => {
      if (res?.payload) {
        setSelectedBill({ paymentId: res?.payload?.data });
        setEditBill(true);
        setRefereneValues({ visible: false, referenceNo: null });
      }
    });
  };

  return (
    <>
      {isEditBill ? (
        <Billing bill={selectedBill} />
      ) : (
        <div className="w100Per">
          <Grid container className="back-white">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="ph20">
              <div className="flex-row-ali-cen">
                <div className="top-row-cen-con w100Per">
                  <div className="w40Per">
                    <CustomTextField
                      placeholder="Search"
                      fullWidth
                      backgroundColor={AppColors.lightBlue2}
                      value={searchValue}
                      handleChange={(e) => setSearchValue(e?.target?.value)}
                    />
                  </div>
                  <div className="ml10">
                    <CustomButton
                      text="Create bill"
                      smallBtn
                      onClick={() => handleCreateBill(false)}
                    />
                  </div>
                  {billingList?.some((bl) => bl?.isSelected) ? (
                    <div className="ml10">
                      <CustomButton
                        text="Pay"
                        smallBtn
                        onClick={handlePayDownload}
                      />
                    </div>
                  ) : null}

                  <div className="w12Per pos-absolute r80">
                    <Select
                      list={billingFilters}
                      value={filterType}
                      handleChange={(e, value) => {
                        setFilterType(
                          value?.value === undefined
                            ? billingFilters?.[0]
                            : value,
                        );
                      }}
                      search
                    />
                  </div>
                </div>
              </div>
              <div className="flex-center mt15">
                <div className="w90Per">
                  <div className="flex-row-ali-cen">
                    <Typography
                      className={`txt-semi-bold fs12 gray-back2 cursor ${
                        secFilter === "All" ? "tab-green-back" : "tab-gray-back"
                      }`}
                      onClick={() => setSecFilter("All")}
                    >
                      All
                    </Typography>
                    <Typography
                      className={`txt-semi-bold fs12 gray-back2 cursor ml15 ${
                        secFilter === "Paid"
                          ? "tab-green-back"
                          : "tab-gray-back"
                      }`}
                      onClick={() => setSecFilter("Paid")}
                    >
                      Paid
                    </Typography>
                    <Typography
                      className={`txt-semi-bold fs12 gray-back2 cursor ml15 ${
                        secFilter === "Unpaid"
                          ? "tab-green-back"
                          : "tab-gray-back"
                      }`}
                      onClick={() => setSecFilter("Unpaid")}
                    >
                      Unpaid
                    </Typography>
                  </div>
                </div>
              </div>
              <div className="flex-center">
                <div className="pb100 w90Per scroll-70vh ph10 mt10">
                  {billingList?.length > 0 ? (
                    billingList?.map((bill, ind) => (
                      <div className="flex-row-ali-cen mv10" key={ind + "bill"}>
                        <div className="w5Per">
                          <CustomCheckbox
                            checked={bill?.isSelected}
                            onChange={(e) => handleCheckItem(!e, ind)}
                            disabled={bill?.status === "paid"}
                          />
                        </div>
                        <div
                          className="w95Per bil-brdr cursor"
                          onClick={() => {
                            if (
                              bill?.appointmentStatus === "completed" ||
                              // bill?.status === "paid" ||
                              !statusActive
                            ) {
                              dispatch(
                                getCompliantSummaryByIdWoLoader(
                                  bill?.appointmentId,
                                ),
                              );
                              setSelectedBill(bill);
                              return setInvModVisible(true);
                            }
                            if (
                              bill?.appointmentStatus !== "completed"
                              //  && bill?.status !== "paid"
                            ) {
                              setSelectedBill(bill);
                              return setEditBill(true);
                            }
                          }}
                        >
                          <div className="flex-row-ali-cen">
                            <div className="w10Per">
                              <div className="flex-column">
                                <Typography className="blue-color txt-semi-bold fs36 flex-center mb10Min">
                                  {moment(new Date(bill?.createdDate)).format(
                                    "DD",
                                  )}
                                </Typography>
                                <Typography className="blue-color txt-semi-bold fs12 flex-center">
                                  {moment(new Date(bill?.createdDate)).format(
                                    "MMM YYYY",
                                  )}
                                </Typography>
                                <div className="flex-center mt5">
                                  <div className="bil-yel-line w100Per" />
                                </div>
                              </div>
                            </div>
                            <div className="w60Per ph10">
                              <div className="flex-column">
                                <Typography className="txt-semi-bold fs14 black21">
                                  {bill?.invoiceNumbers ??
                                    bill?.treatmentInvoiceNumber}
                                </Typography>
                                <Typography className="txt-semi-bold fs14 gray27 capitalize mt5">
                                  {getRightSaluation(
                                    clinicVets,
                                    bill?.doctorId,
                                  )}
                                  {/* {bill?.doctorId === bill?.clinicId
                                    ? ""
                                    : "Dr. "} */}
                                  {bill?.doctorName}
                                </Typography>
                              </div>
                            </div>
                            <div className="w30Per">
                              <div className="flex-end">
                                <div className="flex-column">
                                  <div className="flex-end">
                                    <Typography className="fs14 font-bold blue-color">
                                      Rs.{" "}
                                      {Number(
                                        Number(
                                          roundWithSignedDiff(
                                            getTaxAndSubTotalAndTotalForTreatment(
                                              getTreatmentRecordBasedType(
                                                bill?.paymentDetails,
                                              ),
                                              services?.data,
                                              bill?.procedure ?? [],
                                            )?.subTotal,
                                          )?.rounded +
                                            roundWithSignedDiff(
                                              getTaxAndSubTotalAndTotal(
                                                getPharmacyRecordBasedType(
                                                  bill?.paymentDetails,
                                                ),
                                              )?.subTotal,
                                            )?.rounded +
                                            roundWithSignedDiff(
                                              getTaxAndSubTotalAndTotal(
                                                getPetShopRecordBasedType(
                                                  bill?.paymentDetails,
                                                ),
                                              )?.subTotal,
                                            )?.rounded,
                                        ) - Number(bill?.amtPaid ?? 0),
                                      ) > 0
                                        ? Number(
                                            Number(
                                              roundWithSignedDiff(
                                                getTaxAndSubTotalAndTotalForTreatment(
                                                  getTreatmentRecordBasedType(
                                                    bill?.paymentDetails,
                                                  ),
                                                  services?.data,
                                                  bill?.procedure ?? [],
                                                )?.subTotal,
                                              )?.rounded +
                                                roundWithSignedDiff(
                                                  getTaxAndSubTotalAndTotal(
                                                    getPharmacyRecordBasedType(
                                                      bill?.paymentDetails,
                                                    ),
                                                  )?.subTotal,
                                                )?.rounded +
                                                roundWithSignedDiff(
                                                  getTaxAndSubTotalAndTotal(
                                                    getPetShopRecordBasedType(
                                                      bill?.paymentDetails,
                                                    ),
                                                  )?.subTotal,
                                                )?.rounded,
                                            ) - Number(bill?.amtPaid ?? 0),
                                          )?.toFixed(2)
                                        : "Nil"}
                                    </Typography>
                                  </div>
                                  <div className="flex-row-ali-cen mt10">
                                    <div className="flex1-end">
                                      {bill?.appointmentType ? (
                                        <Typography
                                          className={`br5 ph10 pv5 font-medium  fs12 white-color ${
                                            bill?.appointmentType === "Physical"
                                              ? "card-con-blue-back"
                                              : "virtual-bg-color"
                                          }`}
                                        >
                                          {bill?.appointmentType === "Home"
                                            ? "Home Visit"
                                            : bill?.appointmentType}
                                        </Typography>
                                      ) : null}

                                      <Typography
                                        className={`font-medium fs12 br5 ph10 pv5 ml10 ${
                                          bill?.status === "paid"
                                            ? "green2 green-back2"
                                            : bill?.status === "partiallypaid"
                                              ? "yellow3 yel-back"
                                              : "red13 red-back3"
                                        }`}
                                      >
                                        {bill?.status === "paid"
                                          ? "Paid"
                                          : bill?.status === "partiallypaid"
                                            ? "Partially Paid"
                                            : "Unpaid"}
                                      </Typography>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-rec">No records available</div>
                  )}
                  {Math.ceil(petBilling?.totalRecords / itemsPerLoad) > 1 ? (
                    <div className="flex-end">
                      <Pagination
                        count={Math.ceil(
                          petBilling?.totalRecords / itemsPerLoad,
                        )}
                        variant="outlined"
                        color="primary"
                        page={page}
                        onChange={handleChangePage}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      )}
      <CustomModal
        open={payModalVisible}
        onClose={payModalClose}
        header="Pay"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh">
          <div className="flex-center">
            <Card
              sx={{
                borderRadius: 1,
                padding: 2,
                borderTopColor: `${getBorderColor(
                  modalData?.status ?? "unpaid",
                )}`,
              }}
              className="pay-card"
            >
              <div className="flex-row">
                <div className="w50Per">
                  <Typography className="fs12 txt-semi-bold gray14">
                    Pet Name
                  </Typography>
                  <Typography className="fs14 txt-semi-bold black">
                    {billingList?.[0]?.petName}
                  </Typography>
                </div>
                <div className="w50Per">
                  <Typography className="fs12 txt-semi-bold gray14">
                    Parent Name
                  </Typography>
                  <Typography className="fs14 txt-semi-bold black">
                    {billingList?.[0]?.userName}
                  </Typography>
                </div>
              </div>
              <div className="mh20Min">
                <div className="gry-hor-split3 mv5" />
              </div>
              {billingList
                ?.filter((bil) => bil?.isSelected)
                ?.map((li) => (
                  <div className="mt10" key={li?.paymentId + "invit"}>
                    <Typography className="txt-regular fs14 gray7">
                      {moment(new Date(li?.createdDate)).format("DD MMM, YYYY")}
                    </Typography>
                    <div className="bil-his-box mt5">
                      <div className="flex-row">
                        <div className="flex-start">
                          <Typography className="fs14 font-medium black">
                            {li?.invoiceNumbers}
                          </Typography>
                        </div>
                        <div className="flex1-end">
                          <Typography className="txt-semi-bold fs14 orange3">
                            {Number(
                              Number(
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotalForTreatment(
                                    getTreatmentRecordBasedType(
                                      li?.paymentDetails,
                                    ),
                                    services?.data,
                                    li?.procedure ?? [],
                                  )?.subTotal,
                                )?.rounded +
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(
                                      getPharmacyRecordBasedType(
                                        li?.paymentDetails,
                                      ),
                                    )?.subTotal,
                                  )?.rounded +
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(
                                      getPetShopRecordBasedType(
                                        li?.paymentDetails,
                                      ),
                                    )?.subTotal,
                                  )?.rounded,
                              ) - Number(li?.amtPaid ?? 0),
                            ) > 0
                              ? `Rs ${Number(
                                  Number(
                                    roundWithSignedDiff(
                                      getTaxAndSubTotalAndTotalForTreatment(
                                        getTreatmentRecordBasedType(
                                          li?.paymentDetails,
                                        ),
                                        services?.data,
                                        li?.procedure ?? [],
                                      )?.subTotal,
                                    )?.rounded +
                                      roundWithSignedDiff(
                                        getTaxAndSubTotalAndTotal(
                                          getPharmacyRecordBasedType(
                                            li?.paymentDetails,
                                          ),
                                        )?.subTotal,
                                      )?.rounded +
                                      roundWithSignedDiff(
                                        getTaxAndSubTotalAndTotal(
                                          getPetShopRecordBasedType(
                                            li?.paymentDetails,
                                          ),
                                        )?.subTotal,
                                      )?.rounded,
                                  ) - Number(li?.amtPaid ?? 0),
                                )?.toFixed(2)}`
                              : "Nil"}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              <div className="mt20 bil-bal-con">
                <div className="flex-row">
                  <div>
                    <Typography className="font-bold fs14 red-color">
                      Balance Due
                    </Typography>
                  </div>
                  <div className="flex1-end">
                    <Typography className="font-bold fs14 red-color">
                      Rs{" "}
                      {getSelectedInvoicesBalanceDue(
                        billingList?.filter((bil) => bil?.isSelected),
                        Number(0),
                        services?.data,
                        selectedBill?.procedure ?? [],
                      )?.toFixed(2)}
                    </Typography>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <Grid container spacing={2} className="ph20">
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <CustomTextField
                label="Amount"
                name="amount"
                fullWidth
                handleChange={(e) =>
                  handleChangeValues("amount", e?.target?.value)
                }
                value={payValues?.amount}
                labelTop
                helperText={payErr?.amount}
                error={!!payErr?.amount}
                type="number"
              />
            </Grid>

            <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
              <div className="font-medium fs14 black2">Payment Mode</div>
              <PaymentModeDropdown
                list={
                  (profile?.id || profile?.createdBy) === RMVClinicId
                    ? RMVPaymentTypeList
                    : enabledPaymentTypes
                }
                value={payValues?.paymentMode}
                error={!!payErr?.paymentMode}
                helperText={payErr?.paymentMode}
                onChange={(e, value) => {
                  handleChangeValues("paymentMode", e);
                }}
                name="paymentMode"
                label=""
              />
              {/* <Select
                list={
                  (profile?.id || profile?.createdBy) === RMVClinicId
                    ? RMVPaymentTypeList
                    : enabledPaymentTypes
                }
                value={payValues?.paymentMode}
                handleChange={(e, value) =>
                  handleChangeValues("paymentMode", value)
                }
                name="paymentMode"
                search
                helperText={payErr?.paymentMode}
                error={!!payErr?.paymentMode}
              /> */}
            </Grid>
            {payValues?.paymentMode?.value === "advanceAdjustment" ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="txt-semi-bold fs14 orange5">
                  Available Balance:{" "}
                  {Number(billingList?.[0]?.advanceAmount) > 0
                    ? Number(billingList?.[0]?.advanceAmount)?.toFixed(2)
                    : "-"}
                </Typography>
              </Grid>
            ) : null}

            <div className="flex1-end mt20">
              <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <CustomButton text="Pay" onClick={finalPayment} smallBtn />
              </Grid>
            </div>
          </Grid>
        </div>
      </CustomModal>
      <CustomModal
        open={invModVisible}
        onClose={handleInvModClose}
        header=""
        modal
        modalWidth={90}
      >
        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <div ref={contentToPrint} className="bg">
            {getTreatmentRecordBasedType(selectedBill?.paymentDetails)?.length >
            0 ? (
              <>
                <div className="mt30">
                  <div className="flex-row-ali-cen mv20">
                    <div className="w30Per">
                      <div className="pl20">
                        <div>
                          {clinicDet?.image ? (
                            <img
                              src={clinicDet?.image}
                              className="img-h75"
                              alt=""
                            />
                          ) : (
                            <div className="flex-center h50img">
                              <Typography className="font-bold fs30 white-color capitalize">
                                {clinicDet?.name?.[0]}
                              </Typography>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w40Per flex-center">
                      <div className="font-bold fs18 blue-color">
                        TREATMENT INVOICE
                      </div>
                    </div>
                    <div className="w30Per flex1-end">
                      <div className="flex-column">
                        <div className="font-bold fs12 blue-color">
                          Business Name:
                        </div>
                        <div className="font-medium blue-color fs12">
                          {clinicDet?.name ?? profile?.name ?? ""}
                        </div>
                        <div className="font-bold fs12 blue-color">
                          Business Address:
                        </div>
                        <div className="header-right-text2">
                          {clinicDet?.address ?? ""}
                        </div>
                        {clinicDet?.taxId?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <div className="font-bold fs12 blue-color">
                              CLINIC TAX ID:{" "}
                            </div>
                            <div className="header-right-text2 ml3">
                              {clinicDet?.taxId ?? ""}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                        {clinicDet?.mobile?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <div className="font-bold fs12 blue-color">
                              Contact:{" "}
                            </div>
                            <div className="header-right-text2 ml3">
                              {clinicDet?.mobile ?? ""}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="line" />
                  <div className="flex-row mt10 ph30">
                    <div className="w60Per">
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Parent Name
                        </Typography>
                        <div className="txt-semi-bold fs14 black capitalize ml10">
                          {selectedBill?.userName}
                        </div>
                      </div>
                      {selectedBill?.mobile?.length > 0 ? (
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            Phone Number
                          </Typography>
                          <div className="txt-semi-bold fs14 black capitalize ml10">
                            {selectedBill?.mobile}
                          </div>
                        </div>
                      ) : null}
                      {selectedBill?.apartmentAddress?.length > 0 ? (
                        <div className="flex-row">
                          <Typography className="font-bold fs14 gray4">
                            Address
                          </Typography>

                          <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                            {selectedBill?.apartmentAddress}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex1-end w40Per">
                      <div className="flex-column">
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            Payment Status
                          </Typography>
                          <Typography
                            className={`ml10 fs16 font-bold capitalize ${
                              selectedBill?.status === "paid"
                                ? "green2"
                                : selectedBill?.status === "unpaid"
                                  ? "red2"
                                  : "orange-color"
                            }`}
                          >
                            {selectedBill?.status === "partiallypaid"
                              ? "Partially Paid"
                              : selectedBill?.status}
                          </Typography>
                        </div>
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            Total
                          </Typography>
                          <Typography className="black fs16 font-bold ml10">
                            Rs{" "}
                            {Number(
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotalForTreatment(
                                  getTreatmentRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                  services?.data,
                                  selectedBill?.procedure ?? [],
                                )?.subTotal,
                              )?.rounded +
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotal(
                                    getPharmacyRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )?.subTotal,
                                )?.rounded +
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotal(
                                    getPetShopRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )?.subTotal,
                                )?.rounded,
                            )?.toFixed(2)}
                          </Typography>
                        </div>
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            Amount Paid
                          </Typography>
                          <Typography className="green-color fs16 font-bold ml10">
                            Rs{" "}
                            {Number(selectedBill?.amtPaid) > 0
                              ? Number(selectedBill?.amtPaid)?.toFixed(2)
                              : Number(selectedBill?.amtPaid)}
                          </Typography>
                        </div>
                        <div className="flex-row-ali-cen">
                          <Typography className="font-bold fs14 gray4">
                            Balance Due
                          </Typography>
                          <Typography className="yellow-color fs16 font-bold ml10">
                            Rs{" "}
                            {Number(
                              Number(
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotalForTreatment(
                                    getTreatmentRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                    services?.data,
                                    selectedBill?.procedure ?? [],
                                  )?.subTotal,
                                )?.rounded +
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(
                                      getPharmacyRecordBasedType(
                                        selectedBill?.paymentDetails,
                                      ),
                                    )?.subTotal,
                                  )?.rounded +
                                  roundWithSignedDiff(
                                    getTaxAndSubTotalAndTotal(
                                      getPetShopRecordBasedType(
                                        selectedBill?.paymentDetails,
                                      ),
                                    )?.subTotal,
                                  )?.rounded,
                              ) - Number(selectedBill?.amtPaid ?? 0),
                            )?.toFixed(2)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lower-container">
                    <div className="box-container">
                      <div className="row-container">
                        <div>
                          <div className="colum-header">Pet Name</div>
                          <div className="colum-data">
                            {selectedBill?.petName}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Vet Name</div>
                          <div className="colum-data capitalize">
                            {getRightSaluation(
                              clinicVets,
                              selectedBill?.doctorId,
                            )}
                            {selectedBill?.doctorName}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Invoice Number</div>
                          <div className="colum-data">
                            {selectedBill?.treatmentInvoiceNumber ?? ""}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Invoice Date</div>
                          <div className="colum-data">
                            {moment(new Date(selectedBill?.createdDate)).format(
                              "DD MMM YYYY",
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="thin-line"></div>

                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>HSN/SAC Code</th>
                            <th>SERVICE / ITEM NAME</th>
                            <th>QTY</th>
                            <th>UNIT PRICE</th>
                            {getTreatmentRecordBasedType(
                              selectedBill?.paymentDetails,
                            )?.some((li) => li?.discount > 0) ? (
                              <th>UNIT DISCOUNT (Rs)</th>
                            ) : null}
                            <th>TAX</th>
                            <th>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getTreatmentRecordBasedType(
                            selectedBill?.paymentDetails,
                          )?.length > 0 &&
                            getTreatmentRecordBasedType(
                              selectedBill?.paymentDetails,
                            )
                              ?.filter((pd) => pd?.type === "consultation")
                              ?.map((con) => (
                                <tr>
                                  <td>-</td>
                                  <td>
                                    <div className="flex-row-ali-cen">
                                      Consultation (
                                      {con?.name === "Home"
                                        ? "Home Visit"
                                        : con?.name}
                                      ){" "}
                                      {selectedBill?.doctorName?.length > 0
                                        ? " - "
                                        : ""}
                                      <div className="font-bold fs12 black ml3">
                                        {selectedBill?.doctorName?.length > 0
                                          ? `${getRightSaluation(
                                              clinicVets,
                                              selectedBill?.doctorId,
                                            )}${selectedBill?.doctorName}`
                                          : ""}
                                      </div>
                                      {selectedBill?.registrationNo?.length >
                                      0 ? (
                                        <div className="flex-row-ali-cen">
                                          <div className="font-regular fs12 black ml5">
                                            (
                                          </div>
                                          <div className="font-bold fs12 black ml2">
                                            RegNo:
                                          </div>
                                          <div className="font-regular fs12 black ml5 capitalize">
                                            {selectedBill?.registrationNo ??
                                              selectedBill?.registrationNo}{" "}
                                            )
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>1</td>
                                  <td>Rs {Number(con?.price)?.toFixed(2)}</td>
                                  {getTreatmentRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  )?.some((li) => li?.discount > 0) ? (
                                    <td>
                                      {con?.discount > 0
                                        ? Number(con?.discount)?.toFixed(2)
                                        : "-"}
                                    </td>
                                  ) : null}
                                  <td>
                                    {con?.tax > 0
                                      ? `Rs ${Number(con?.tax)?.toFixed(2)}`
                                      : "-"}
                                  </td>
                                  <td>Rs {Number(con?.total)?.toFixed(2)}</td>
                                </tr>
                              ))}

                          {getTreatmentRecordBasedType(
                            selectedBill?.paymentDetails,
                          )?.length > 0 &&
                            reorderList(
                              getTreatmentRecordBasedType(
                                selectedBill?.paymentDetails,
                              ),
                            )
                              ?.filter((pd) =>
                                pd?.type !== "consultation" &&
                                pd?.type?.toLowerCase() !== "consumables" &&
                                selectedBill?.showSublineItems
                                  ? true
                                  : pd?.patientWorkbook
                                    ? pd?.mainProcedure
                                    : true,
                              )
                              ?.map((itm, i) => {
                                const isSubItem =
                                  (itm?.procedureId ?? "")?.toString()?.length >
                                    0 &&
                                  // itm?.patientWorkbook &&
                                  !itm?.mainProcedure;
                                const showSubPrice =
                                  selectedBill?.showSublinePrice;
                                let stock = null;
                                if (itm?.batchId) {
                                  stock = stocks?.data?.find(
                                    (stc) => stc?.stockId === itm?.stockId,
                                  );
                                }
                                if (itm?.serviceId) {
                                  stock = services?.data?.find(
                                    (ser) => ser?.serviceId === itm?.serviceId,
                                  );
                                }
                                const reqTotal = showSubPrice
                                  ? (itm?.total ?? 0)
                                  : itm?.list?.length > 0
                                    ? Number(
                                        Number(
                                          itm?.list
                                            ?.filter((li) =>
                                              selectedBill?.showSublinePrice
                                                ? li?.type?.toLowerCase() !==
                                                  "consumables"
                                                : true,
                                            )
                                            ?.reduce(
                                              (acc, li) =>
                                                acc + Number(li?.total),
                                              0,
                                            ) ?? 0,
                                        ) + Number(itm?.total ?? 0),
                                      )
                                    : (itm?.total ?? 0);
                                const reqUnitPrice = showSubPrice
                                  ? (itm?.mrpPrice ?? itm?.price ?? 0)
                                  : itm?.list?.length > 0
                                    ? Number(
                                        Number(
                                          itm?.list
                                            ?.filter((li) =>
                                              selectedBill?.showSublinePrice
                                                ? li?.type?.toLowerCase() !==
                                                  "consumables"
                                                : true,
                                            )
                                            ?.reduce(
                                              (acc, li) =>
                                                acc +
                                                Number(
                                                  li?.mrpPrice ?? li?.price,
                                                ),
                                              0,
                                            ) ?? 0,
                                        ) +
                                          Number(
                                            itm?.mrpPrice ?? itm?.price ?? 0,
                                          ),
                                      )
                                    : (itm?.mrpPrice ?? itm?.price ?? 0);

                                const reqDiscount = showSubPrice
                                  ? (itm?.discount ?? 0)
                                  : itm?.list?.length > 0
                                    ? Number(
                                        Number(
                                          itm?.list
                                            ?.filter((li) =>
                                              selectedBill?.showSublinePrice
                                                ? li?.type?.toLowerCase() !==
                                                  "consumables"
                                                : true,
                                            )
                                            ?.reduce(
                                              (acc, li) =>
                                                acc + Number(li?.discount),
                                              0,
                                            ) ?? 0,
                                        ) + Number(itm?.discount ?? 0),
                                      )
                                    : (itm?.discount ?? 0);
                                if (
                                  isSubItem &&
                                  !selectedBill?.showSublineItems
                                ) {
                                  return null;
                                }
                                return (
                                  <>
                                    <tr key={i}>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          {stock?.hsnSacCode?.length > 0
                                            ? stock?.hsnSacCode
                                            : "-"}
                                        </div>
                                      </td>
                                      <td>
                                        <div
                                          className={
                                            isSubItem
                                              ? ""
                                              : "fs12 font-bold black"
                                          }
                                        >
                                          {itm?.name}
                                        </div>
                                      </td>
                                      <td>
                                        <div
                                          className={
                                            isSubItem
                                              ? ""
                                              : "fs12 font-bold black"
                                          }
                                        >
                                          {itm?.quantity}
                                          {itm?.stockId && itm?.batchId
                                            ? `${getRightPackageUom(
                                                getBatch(
                                                  itm?.stockId,
                                                  itm?.batchId,
                                                )?.packageUom,
                                              )}`
                                            : ""}
                                        </div>
                                      </td>
                                      <td>
                                        {isSubItem && !showSubPrice ? null : (
                                          <div
                                            className={
                                              isSubItem
                                                ? ""
                                                : "fs12 font-bold black"
                                            }
                                          >
                                            Rs{" "}
                                            {Number(
                                              !showSubPrice
                                                ? reqTotal
                                                : reqUnitPrice,
                                            )?.toFixed(2)}
                                          </div>
                                        )}
                                      </td>
                                      {getTreatmentRecordBasedType(
                                        selectedBill?.paymentDetails,
                                      )?.some((li) => li?.discount > 0) ? (
                                        <td>
                                          {isSubItem && !showSubPrice ? null : (
                                            <div
                                              className={
                                                isSubItem
                                                  ? ""
                                                  : "fs12 font-bold black"
                                              }
                                            >
                                              {Number(reqDiscount) > 0
                                                ? Number(reqDiscount)?.toFixed(
                                                    2,
                                                  )
                                                : "-"}
                                            </div>
                                          )}
                                        </td>
                                      ) : null}
                                      <td>
                                        {isSubItem && !showSubPrice ? null : (
                                          <div className="fs12 font-bold black">
                                            {itm?.tax > 0
                                              ? `Rs ${Number(itm?.tax)?.toFixed(
                                                  2,
                                                )}`
                                              : "-"}
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        {isSubItem && !showSubPrice ? null : (
                                          <div
                                            className={
                                              isSubItem
                                                ? ""
                                                : "fs12 font-bold black"
                                            }
                                          >
                                            Rs {Number(reqTotal)?.toFixed(2)}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  </>
                                );
                              })}
                          {selectedBill?.showSublineItems &&
                            reorderList(
                              getTreatmentRecordBasedType(
                                selectedBill?.paymentDetails,
                              ),
                            )?.filter(
                              (pd) => pd?.type?.toLowerCase() === "consumables",
                            )?.length > 0 &&
                            reorderList(
                              getTreatmentRecordBasedType(
                                selectedBill?.paymentDetails,
                              ),
                            )
                              ?.filter(
                                (pd) =>
                                  pd?.type?.toLowerCase() === "consumables",
                              )
                              ?.slice(0, 1)
                              ?.map((itm, i) => {
                                const reqTotal =
                                  reorderList(
                                    getTreatmentRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )
                                    ?.filter(
                                      (pd) =>
                                        pd?.type?.toLowerCase() ===
                                        "consumables",
                                    )
                                    ?.reduce(
                                      (acc, li) => acc + Number(li?.total),
                                      0,
                                    ) ?? 0;

                                const reqUnitPrice =
                                  reorderList(
                                    getTreatmentRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )
                                    ?.filter(
                                      (pd) =>
                                        pd?.type?.toLowerCase() ===
                                        "consumables",
                                    )
                                    ?.reduce(
                                      (acc, li) =>
                                        acc +
                                        Number(
                                          Number(
                                            li?.mrpPrice ?? li?.price ?? 0,
                                          ) * Number(li?.quantity ?? 1),
                                        ),
                                      0,
                                    ) ?? 0;

                                const reqDiscount =
                                  reorderList(
                                    getTreatmentRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )
                                    ?.filter(
                                      (pd) =>
                                        pd?.type?.toLowerCase() ===
                                        "consumables",
                                    )
                                    ?.reduce(
                                      (acc, li) => acc + Number(li?.discount),
                                      0,
                                    ) ?? 0;

                                return (
                                  <>
                                    <tr key={i}>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          -
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          Medical Consumables
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fs12 font-bold black">
                                          1
                                        </div>
                                      </td>
                                      <td>
                                        {selectedBill?.showSublinePrice ? (
                                          <div className="fs12 font-bold black">
                                            Rs{" "}
                                            {Number(reqUnitPrice)?.toFixed(2)}
                                          </div>
                                        ) : null}
                                      </td>
                                      {getTreatmentRecordBasedType(
                                        selectedBill?.paymentDetails,
                                      )?.some((li) => li?.discount > 0) ? (
                                        <td>
                                          <div className="fs12 font-bold black">
                                            {Number(reqDiscount) > 0
                                              ? Number(reqDiscount)?.toFixed(2)
                                              : "-"}
                                          </div>
                                        </td>
                                      ) : null}
                                      <td>
                                        {selectedBill?.showSublinePrice ? (
                                          <div className="fs12 font-bold black">
                                            {Number(
                                              getTaxAndSubTotalAndTotalForTreatment(
                                                getTreatmentRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                )?.filter(
                                                  (pd) =>
                                                    pd?.type?.toLowerCase() ===
                                                    "consumables",
                                                ),
                                              )?.tax ?? 0,
                                            ) > 0
                                              ? Number(
                                                  getTaxAndSubTotalAndTotalForTreatment(
                                                    getTreatmentRecordBasedType(
                                                      selectedBill?.paymentDetails,
                                                    )?.filter(
                                                      (pd) =>
                                                        pd?.type?.toLowerCase() ===
                                                        "consumables",
                                                    ),
                                                  )?.tax,
                                                )?.toFixed(2)
                                              : "-"}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td>
                                        {selectedBill?.showSublinePrice ? (
                                          <div className="fs12 font-bold black">
                                            Rs {Number(reqTotal)?.toFixed(2)}
                                          </div>
                                        ) : null}
                                      </td>
                                    </tr>
                                  </>
                                );
                              })}

                          <tr>
                            <td colSpan="12">
                              <hr className="thin-line2" />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="12">
                              <div className="flex-row-between">
                                <div className="w65Per ali-itm-flx-end">
                                  <div className="flex-column w100Per">
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="txt-semi-bold fs12 black">
                                        Person In Charge:
                                      </div>
                                      <Typography className="font-bold fs12 gray4 ml10">
                                        {clinicDet?.name}
                                      </Typography>
                                    </div>
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="txt-semi-bold fs12 black">
                                        Disclaimer:
                                      </div>
                                      <Typography className="fs12 txt-regular gray-color ml10">
                                        {clinicDet?.disclaimer}
                                      </Typography>
                                    </div>
                                  </div>
                                </div>
                                <div className="w35Per flex-end pr10">
                                  <div className="flex-column w100Per">
                                    {Number(
                                      getTaxAndSubTotalAndTotalForTreatment(
                                        getTreatmentRecordBasedType(
                                          selectedBill?.paymentDetails,
                                        ),
                                        services?.data,
                                        selectedBill?.procedure ?? [],
                                      )?.discount,
                                    ) > 0 ? (
                                      <div className="flex-row-ali-cen mb5">
                                        <div className="w70Per">
                                          <div className="txtstyle">
                                            Discount
                                          </div>
                                        </div>
                                        <div className="w30Per flex-end">
                                          <div className="txtstyle">
                                            {Number(
                                              getTaxAndSubTotalAndTotalForTreatment(
                                                getTreatmentRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                                services?.data,
                                                selectedBill?.procedure ?? [],
                                              )?.discount,
                                            ) > 0
                                              ? `Rs ${Number(
                                                  getTaxAndSubTotalAndTotalForTreatment(
                                                    getTreatmentRecordBasedType(
                                                      selectedBill?.paymentDetails,
                                                    ),
                                                    services?.data,
                                                    selectedBill?.procedure ??
                                                      [],
                                                  )?.discount,
                                                ).toFixed(2)}`
                                              : "-"}
                                          </div>
                                        </div>
                                      </div>
                                    ) : null}
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Treatment Subtotal
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          Rs{" "}
                                          {Number(
                                            (getTaxAndSubTotalAndTotalForTreatment(
                                              getTreatmentRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                              services?.data,
                                              selectedBill?.procedure ?? [],
                                            )?.subTotal ?? 0) -
                                              (getTaxAndSubTotalAndTotalForTreatment(
                                                getTreatmentRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                                services?.data,
                                                selectedBill?.procedure ?? [],
                                              )?.tax ?? 0) +
                                              (getTaxAndSubTotalAndTotalForTreatment(
                                                getTreatmentRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                                services?.data,
                                                selectedBill?.procedure ?? [],
                                              )?.discount ?? 0),
                                          )?.toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Taxes (CGST)
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          {Number(
                                            getTaxAndSubTotalAndTotalForTreatment(
                                              getTreatmentRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                              services?.data,
                                              selectedBill?.procedure ?? [],
                                            )?.tax,
                                          ) /
                                            2 >
                                          0
                                            ? `Rs ${Number(
                                                Number(
                                                  getTaxAndSubTotalAndTotalForTreatment(
                                                    getTreatmentRecordBasedType(
                                                      selectedBill?.paymentDetails,
                                                    ),
                                                    services?.data,
                                                    selectedBill?.procedure ??
                                                      [],
                                                  )?.tax,
                                                ) / 2,
                                              )?.toFixed(2)}`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Taxes (SGST)
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          {Number(
                                            getTaxAndSubTotalAndTotalForTreatment(
                                              getTreatmentRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                              services?.data,
                                              selectedBill?.procedure ?? [],
                                            )?.tax,
                                          ) /
                                            2 >
                                          0
                                            ? `Rs ${Number(
                                                Number(
                                                  getTaxAndSubTotalAndTotalForTreatment(
                                                    getTreatmentRecordBasedType(
                                                      selectedBill?.paymentDetails,
                                                    ),
                                                    services?.data,
                                                    selectedBill?.procedure ??
                                                      [],
                                                  )?.tax,
                                                ) / 2,
                                              )?.toFixed(2)}`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Round Off
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          {`Rs ${Number(
                                            Number(
                                              roundWithSignedDiff(
                                                getTaxAndSubTotalAndTotalForTreatment(
                                                  getTreatmentRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                  services?.data,
                                                  selectedBill?.procedure ?? [],
                                                )?.subTotal,
                                              )?.difference,
                                            ),
                                          )?.toFixed(2)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="balancecontainer ph5 mt10">
                                      <div className="flex-row-ali-cen w100Per">
                                        <div className="w60Per">
                                          <div className="txt-semi-bold fs14 blue-color">
                                            Total
                                          </div>
                                        </div>
                                        <div className="w40Per flex-end">
                                          <div className="txt-semi-bold fs14 blue-color">
                                            Rs{" "}
                                            {Number(
                                              roundWithSignedDiff(
                                                getTaxAndSubTotalAndTotalForTreatment(
                                                  getTreatmentRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                  services?.data,
                                                  selectedBill?.procedure ?? [],
                                                )?.subTotal,
                                              )?.rounded,
                                            )?.toFixed(2)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          <div className="mb20" />
                        </tbody>
                      </table>
                      <div className="flex-row mv20 ph10">
                        <img
                          src={require("../../../../assets/images/jpg/WoofQrCode.jpeg")}
                          alt=""
                          className="img-h50"
                        />
                        <div className="blu-back wh50 flex-center">
                          <img
                            src={require("../../../../assets/images/png/VetInstantLogo.png")}
                            alt=""
                            className="img-h40"
                          />
                        </div>
                        <div className="flex-center">
                          <div className="font-bold fs14 ml20">
                            Scan to download our app
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="page-break" />
              </>
            ) : null}
            {getPharmacyRecordBasedType(selectedBill?.paymentDetails)?.length >
            0 ? (
              <div>
                <div className="flex-row-ali-cen mv20">
                  <div className="w30Per">
                    <div className="pl20">
                      <div>
                        {clinicDet?.image ? (
                          <img
                            src={clinicDet?.image}
                            className="img-h75"
                            alt=""
                          />
                        ) : (
                          <div className="flex-center h50img">
                            <Typography className="font-bold fs30 white-color capitalize">
                              {clinicDet?.name?.[0]}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w40Per flex-center">
                    <div className="font-bold fs18 blue-color">
                      PHARMACY INVOICE
                    </div>
                  </div>
                  <div className="w30Per flex1-end">
                    <div className="flex-column">
                      <div className="font-bold fs12 blue-color">
                        Business Name:
                      </div>
                      <div className="font-medium blue-color fs12">
                        {clinicDet?.name ?? profile?.name ?? ""}
                      </div>
                      <div className="font-bold fs12 blue-color">
                        Business Address:
                      </div>
                      <div className="header-right-text2">
                        {clinicDet?.address ?? ""}
                      </div>
                      {clinicDet?.pharmacyTaxId?.length > 0 ? (
                        <div className="flex-row-ali-cen">
                          <div className="font-bold fs12 blue-color">
                            PHARMACY TAX ID:{" "}
                          </div>
                          <div className="header-right-text2 ml3">
                            {clinicDet?.pharmacyTaxId ?? ""}
                          </div>
                        </div>
                      ) : (
                        ""
                      )}
                      {clinicDet?.mobile?.length > 0 ? (
                        <div className="flex-row-ali-cen">
                          <div className="font-bold fs12 blue-color">
                            Contact:{" "}
                          </div>
                          <div className="header-right-text2 ml3">
                            {clinicDet?.mobile ?? ""}
                          </div>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </div>

                <div className="line" />
                <div className="flex-row mt10 ph30">
                  <div className="w60Per">
                    <div className="flex-row-ali-cen">
                      <Typography className="font-bold fs14 gray4">
                        Parent Name
                      </Typography>
                      <div className="txt-semi-bold fs14 black capitalize ml10">
                        {selectedBill?.userName}
                      </div>
                    </div>
                    {selectedBill?.mobile?.length > 0 ? (
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Phone Number
                        </Typography>
                        <div className="txt-semi-bold fs14 black capitalize ml10">
                          {selectedBill?.mobile}
                        </div>
                      </div>
                    ) : null}
                    {selectedBill?.apartmentAddress?.length > 0 ? (
                      <div className="flex-row">
                        <Typography className="font-bold fs14 gray4">
                          Address
                        </Typography>

                        <div className="txt-semi-bold fs14 black capitalize ml10 w50Per flex-row-wrap">
                          {selectedBill?.apartmentAddress}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex1-end w40Per">
                    <div className="flex-column">
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Payment Status
                        </Typography>
                        <Typography
                          className={`ml10 fs16 font-bold capitalize ${
                            selectedBill?.status === "paid"
                              ? "green2"
                              : selectedBill?.status === "unpaid"
                                ? "red2"
                                : "orange-color"
                          }`}
                        >
                          {selectedBill?.status === "partiallypaid"
                            ? "Partially Paid"
                            : selectedBill?.status}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Total
                        </Typography>
                        <Typography className="black fs16 font-bold ml10">
                          Rs{" "}
                          {Number(
                            roundWithSignedDiff(
                              getTaxAndSubTotalAndTotalForTreatment(
                                getTreatmentRecordBasedType(
                                  selectedBill?.paymentDetails,
                                ),
                                services?.data,
                                selectedBill?.procedure ?? [],
                              )?.subTotal,
                            )?.rounded +
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotal(
                                  getPharmacyRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                )?.subTotal,
                              )?.rounded +
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotal(
                                  getPetShopRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                )?.subTotal,
                              )?.rounded,
                          )?.toFixed(2)}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Amount Paid
                        </Typography>
                        <Typography className="green-color fs16 font-bold ml10">
                          Rs{" "}
                          {Number(selectedBill?.amtPaid) > 0
                            ? Number(selectedBill?.amtPaid)?.toFixed(2)
                            : Number(selectedBill?.amtPaid)}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Balance Due
                        </Typography>
                        <Typography className="yellow-color fs16 font-bold ml10">
                          Rs{" "}
                          {Number(
                            Number(
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotalForTreatment(
                                  getTreatmentRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                  services?.data,
                                  selectedBill?.procedure ?? [],
                                )?.subTotal,
                              )?.rounded +
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotal(
                                    getPharmacyRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )?.subTotal,
                                )?.rounded +
                                roundWithSignedDiff(
                                  getTaxAndSubTotalAndTotal(
                                    getPetShopRecordBasedType(
                                      selectedBill?.paymentDetails,
                                    ),
                                  )?.subTotal,
                                )?.rounded,
                            ) - Number(selectedBill?.amtPaid ?? 0),
                          )?.toFixed(2)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lower-container">
                  <div className="box-container">
                    <div className="row-container">
                      <div>
                        <div className="colum-header">Pet Name</div>
                        <div className="colum-data">
                          {selectedBill?.petName}
                        </div>
                      </div>
                      <div>
                        <div className="colum-header">Vet Name</div>
                        <div className="colum-data capitalize">
                          {getRightSaluation(
                            clinicVets,
                            selectedBill?.doctorId,
                          )}
                          {/* {selectedBill?.doctorId === selectedBill?.clinicId
                            ? ""
                            : "Dr. "} */}
                          {selectedBill?.doctorName}
                        </div>
                      </div>
                      <div>
                        <div className="colum-header">Invoice Number</div>
                        <div className="colum-data">
                          {selectedBill?.invoiceNumbers ?? ""}
                        </div>
                      </div>
                      <div>
                        <div className="colum-header">Invoice Date</div>
                        <div className="colum-data">
                          {moment(new Date(selectedBill?.createdDate)).format(
                            "DD MMM YYYY",
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="thin-line"></div>

                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>HSN/SAC Code</th>
                          <th>SERVICE / ITEM NAME</th>
                          <th>QTY</th>
                          <th>UNIT PRICE</th>
                          {getPharmacyRecordBasedType(
                            selectedBill?.paymentDetails,
                          )?.some((li) => li?.discount > 0) ? (
                            <th>UNIT DISCOUNT (Rs)</th>
                          ) : null}
                          <th>TAX</th>
                          <th>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPharmacyRecordBasedType(
                          selectedBill?.paymentDetails,
                        )?.length > 0 &&
                          getPharmacyRecordBasedType(
                            selectedBill?.paymentDetails,
                          )
                            ?.filter((pd) => pd?.type !== "consultation")
                            ?.map((itm, i) => {
                              let stock = null;
                              if (itm?.batchId) {
                                stock = stocks?.data?.find(
                                  (stc) => stc?.stockId === itm?.stockId,
                                );
                              }
                              if (itm?.serviceId) {
                                stock = services?.data?.find(
                                  (ser) => ser?.serviceId === itm?.serviceId,
                                );
                              }

                              return (
                                <tr key={i}>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {stock?.hsnSacCode?.length > 0
                                        ? stock?.hsnSacCode
                                        : "-"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {itm?.name}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {itm?.quantity}
                                      {itm?.stockId && itm?.batchId
                                        ? `${getRightPackageUom(
                                            getBatch(itm?.stockId, itm?.batchId)
                                              ?.packageUom,
                                          )}`
                                        : ""}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      Rs{" "}
                                      {Number(
                                        itm?.mrpPrice ?? itm?.price,
                                      )?.toFixed(2)}
                                    </div>
                                  </td>
                                  {getPharmacyRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  )?.some((li) => li?.discount > 0) ? (
                                    <td>
                                      <div className="fs12 font-bold black">
                                        {Number(
                                          itm?.mrpDiscount ?? itm?.discount,
                                        ) > 0
                                          ? Number(
                                              itm?.mrpDiscount ?? itm?.discount,
                                            )?.toFixed(2)
                                          : "-"}
                                      </div>
                                    </td>
                                  ) : null}
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {clinicDet?.pharmacyTaxId?.length > 0
                                        ? itm?.tax > 0
                                          ? `Rs ${Number(itm?.tax)?.toFixed(2)}`
                                          : "-"
                                        : "-"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      Rs {Number(itm?.total)?.toFixed(2)}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        <tr>
                          <td colSpan="12">
                            <hr className="thin-line2" />
                          </td>
                        </tr>
                        <tr>
                          <td colSpan="12">
                            <div className="flex-row-between">
                              <div className="w65Per ali-itm-flx-end">
                                <div className="flex-column w100Per">
                                  <div className="flex-row-ali-cen mb5">
                                    <div className="txt-semi-bold fs12 black">
                                      Person In Charge:
                                    </div>
                                    <Typography className="font-bold fs12 gray4 ml10">
                                      {clinicDet?.name}
                                    </Typography>
                                  </div>
                                  <div className="flex-row-ali-cen mb5">
                                    <div className="txt-semi-bold fs12 black">
                                      Disclaimer:
                                    </div>
                                    <Typography className="fs12 txt-regular gray-color ml10">
                                      {clinicDet?.disclaimer}
                                    </Typography>
                                  </div>
                                </div>
                              </div>
                              <div className="w35Per flex-end pr10">
                                <div className="flex-column w100Per">
                                  {Number(
                                    getTaxAndSubTotalAndTotal(
                                      getPharmacyRecordBasedType(
                                        selectedBill?.paymentDetails,
                                      ),
                                    )?.discount,
                                  ) > 0 ? (
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="w70Per">
                                        <div className="txtstyle">Discount</div>
                                      </div>
                                      <div className="w30Per flex-end">
                                        <div className="txtstyle">
                                          {Number(
                                            getTaxAndSubTotalAndTotal(
                                              getPharmacyRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                            )?.discount,
                                          ) > 0
                                            ? `Rs ${Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPharmacyRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.discount,
                                              ).toFixed(2)}`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                  <div className="flex-row-ali-cen mv5">
                                    <div className="w60Per">
                                      <div className="txtstyle">
                                        Pharmacy Subtotal
                                      </div>
                                    </div>
                                    <div className="w40Per flex-end">
                                      <div className="txtstyle">
                                        Rs{" "}
                                        {Number(
                                          getTaxAndSubTotalAndTotal(
                                            getPharmacyRecordBasedType(
                                              selectedBill?.paymentDetails,
                                            ),
                                          )?.subTotal -
                                            getTaxAndSubTotalAndTotal(
                                              getPharmacyRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                            )?.tax +
                                            getTaxAndSubTotalAndTotal(
                                              getPharmacyRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                            )?.discount,
                                        )?.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-row-ali-cen mv5">
                                    <div className="w60Per">
                                      <div className="txtstyle">
                                        Taxes (CGST)
                                      </div>
                                    </div>
                                    <div className="w40Per flex-end">
                                      <div className="txtstyle">
                                        {Number(
                                          getTaxAndSubTotalAndTotal(
                                            getPharmacyRecordBasedType(
                                              selectedBill?.paymentDetails,
                                            ),
                                          )?.tax,
                                        ) /
                                          2 >
                                        0
                                          ? `Rs ${Number(
                                              Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPharmacyRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.tax,
                                              ) / 2,
                                            )?.toFixed(2)}`
                                          : "-"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-row-ali-cen mv5">
                                    <div className="w60Per">
                                      <div className="txtstyle">
                                        Taxes (SGST)
                                      </div>
                                    </div>
                                    <div className="w40Per flex-end">
                                      <div className="txtstyle">
                                        {Number(
                                          getTaxAndSubTotalAndTotal(
                                            getPharmacyRecordBasedType(
                                              selectedBill?.paymentDetails,
                                            ),
                                          )?.tax,
                                        ) /
                                          2 >
                                        0
                                          ? `Rs ${Number(
                                              Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPharmacyRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.tax,
                                              ) / 2,
                                            )?.toFixed(2)}`
                                          : "-"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-row-ali-cen mv5">
                                    <div className="w60Per">
                                      <div className="txtstyle">Round Off</div>
                                    </div>
                                    <div className="w40Per flex-end">
                                      <div className="txtstyle">
                                        {`Rs ${Number(
                                          Number(
                                            roundWithSignedDiff(
                                              getTaxAndSubTotalAndTotal(
                                                getPharmacyRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                              )?.subTotal,
                                            )?.difference,
                                          ),
                                        )?.toFixed(2)}`}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="balancecontainer ph5 mt10">
                                    <div className="flex-row-ali-cen w100Per">
                                      <div className="w60Per">
                                        <div className="txt-semi-bold fs14 blue-color">
                                          Total
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txt-semi-bold fs14 blue-color">
                                          Rs{" "}
                                          {Number(
                                            roundWithSignedDiff(
                                              getTaxAndSubTotalAndTotal(
                                                getPharmacyRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                              )?.subTotal,
                                            )?.rounded,
                                          )?.toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                        <div className="mb20" />
                      </tbody>
                    </table>
                    <div className="flex-row mv20 ph10">
                      <img
                        src={require("../../../../assets/images/jpg/WoofQrCode.jpeg")}
                        alt=""
                        className="img-h50"
                      />
                      <div className="blu-back wh50 flex-center">
                        <img
                          src={require("../../../../assets/images/png/VetInstantLogo.png")}
                          alt=""
                          className="img-h40"
                        />
                      </div>
                      <div className="flex-center">
                        <div className="font-bold fs14 ml20">
                          Scan to download our app
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {getPetShopRecordBasedType(selectedBill?.paymentDetails)?.length >
            0 ? (
              <>
                <div className="mt30">
                  <div className="flex-row-ali-cen mv20">
                    <div className="w30Per">
                      <div className="pl20">
                        <div>
                          {clinicDet?.image ? (
                            <img
                              src={clinicDet?.image}
                              className="img-h75"
                              alt=""
                            />
                          ) : (
                            <div className="flex-center h50img">
                              <Typography className="font-bold fs30 white-color capitalize">
                                {clinicDet?.name?.[0]}
                              </Typography>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w40Per flex-center">
                      <div className="font-bold fs18 blue-color">
                        TAX INVOICE
                      </div>
                    </div>
                    <div className="w30Per flex1-end">
                      <div className="flex-column">
                        <div className="font-bold fs12 blue-color">
                          Business Name:
                        </div>
                        <div className="font-medium blue-color fs12">
                          {clinicDet?.name ?? profile?.name ?? ""}
                        </div>
                        <div className="font-bold fs12 blue-color">
                          Business Address:
                        </div>
                        <div className="header-right-text2">
                          {clinicDet?.address ?? ""}
                        </div>
                        {clinicDet?.taxId?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <div className="font-bold fs12 blue-color">
                              PET SHOP TAX ID:
                            </div>
                            <div className="header-right-text2 ml3">
                              {clinicDet?.petShopTaxId ?? ""}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                        {clinicDet?.mobile?.length > 0 ? (
                          <div className="flex-row-ali-cen">
                            <div className="font-bold fs12 blue-color">
                              Contact:{" "}
                            </div>
                            <div className="header-right-text2 ml3">
                              {clinicDet?.mobile ?? ""}
                            </div>
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="line" />
                  <div className="flex1-end mt10 ph30">
                    <div className="flex-column">
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Payment Status
                        </Typography>
                        <Typography
                          className={`ml10 fs16 font-bold capitalize ${
                            selectedBill?.status === "paid"
                              ? "green2"
                              : selectedBill?.status === "unpaid"
                                ? "red2"
                                : "orange-color"
                          }`}
                        >
                          {selectedBill?.status === "partiallypaid"
                            ? "Partially Paid"
                            : selectedBill?.status}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Total
                        </Typography>
                        <Typography className="black fs16 font-bold ml10">
                          {`Rs. ${Number(
                            roundWithSignedDiff(
                              getTaxAndSubTotalAndTotalForTreatment(
                                getTreatmentRecordBasedType(
                                  selectedBill?.paymentDetails,
                                ),
                                services?.data,
                                selectedBill?.procedure ?? [],
                              )?.subTotal,
                            )?.rounded +
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotal(
                                  getPharmacyRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                )?.subTotal,
                              )?.rounded +
                              roundWithSignedDiff(
                                getTaxAndSubTotalAndTotal(
                                  getPetShopRecordBasedType(
                                    selectedBill?.paymentDetails,
                                  ),
                                )?.subTotal,
                              )?.rounded,
                          )?.toFixed(2)}`}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="font-bold fs14 gray4">
                          Amount Paid
                        </Typography>
                        <Typography className="green-color fs16 font-bold ml10">
                          Rs{" "}
                          {Number(selectedBill?.amtPaid) > 0
                            ? Number(selectedBill?.amtPaid)?.toFixed(2)
                            : Number(selectedBill?.amtPaid)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className="lower-container">
                    <div className="box-container">
                      <div className="row-container">
                        <div>
                          <div className="colum-header">Pet Name</div>
                          <div className="colum-data">
                            {selectedBill?.petName}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Parent Name</div>
                          <div className="colum-data capitalize">
                            {selectedBill?.userName}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Invoice Number</div>
                          <div className="colum-data">
                            {selectedBill?.petShopInvoiceNumber ?? ""}
                          </div>
                        </div>
                        <div>
                          <div className="colum-header">Invoice Date</div>
                          <div className="colum-data">
                            {moment(new Date()).format("DD MMM YYYY")}
                          </div>
                        </div>
                      </div>

                      <div className="thin-line" />

                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>SERVICE / ITEM NAME</th>
                            <th>QTY</th>
                            <th>UNIT PRICE</th>
                            <th>UNIT DISCOUNT (Rs)</th>
                            <th>TAX</th>
                            <th>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPetShopRecordBasedType(
                            selectedBill?.paymentDetails,
                          )
                            ?.filter((li) => li?.quantity > 0)
                            ?.map((li) => {
                              return (
                                <tr>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {li?.name}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {li?.quantity}
                                      {li?.stockId && li?.batchId
                                        ? `${getRightPackageUom(
                                            getBatch(li?.stockId, li?.batchId)
                                              ?.packageUom,
                                          )}`
                                        : ""}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      Rs{" "}
                                      {Number(
                                        li?.mrpPrice ?? li?.price,
                                      )?.toFixed(2)}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {Number(li?.discount) > 0
                                        ? `Rs ${Number(li?.discount)?.toFixed(
                                            2,
                                          )}`
                                        : "-"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      {Number(li?.tax) > 0
                                        ? `Rs ${Number(li?.tax)?.toFixed(2)}`
                                        : "-"}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="fs12 font-bold black">
                                      Rs {Number(li?.total)?.toFixed(2)}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          <tr>
                            <td colSpan="6">
                              <hr className="thin-line2" />
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="12">
                              <div className="flex-row-between">
                                <div className="w65Per ali-itm-flx-end">
                                  <div className="flex-column w100Per">
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="txt-semi-bold fs12 black">
                                        Person In Charge:
                                      </div>
                                      <Typography className="font-bold fs12 gray4 ml10">
                                        {clinicDet?.name}
                                      </Typography>
                                    </div>
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="txt-semi-bold fs12 black">
                                        Disclaimer:
                                      </div>
                                      <Typography className="fs12 txt-regular gray-color ml10">
                                        {clinicDet?.disclaimer}
                                      </Typography>
                                    </div>
                                  </div>
                                </div>
                                <div className="w35Per flex-end pr10">
                                  <div className="flex-column w100Per">
                                    <div className="flex-row-ali-cen mb5">
                                      <div className="w70Per">
                                        <div className="txtstyle">Discount</div>
                                      </div>
                                      <div className="w30Per flex-end">
                                        <div className="txtstyle">
                                          {Number(
                                            getTaxAndSubTotalAndTotal(
                                              getPetShopRecordBasedType(
                                                selectedBill?.paymentDetails,
                                              ),
                                            )?.discount,
                                          ) > 0
                                            ? `Rs. ${Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.discount,
                                              ).toFixed(2)}`
                                            : "-"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">Subtotal</div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          Rs{" "}
                                          {Number(
                                            Number(
                                              getTaxAndSubTotalAndTotal(
                                                getPetShopRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                              )?.subTotal ?? 0,
                                            ) -
                                              (getTaxAndSubTotalAndTotal(
                                                getPetShopRecordBasedType(
                                                  selectedBill?.paymentDetails,
                                                ),
                                              )?.tax ?? 0) +
                                              Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.discount,
                                              ),
                                          )?.toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Taxes (CGST)
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          Rs{" "}
                                          {clinicDet?.petShopTaxId?.length > 0
                                            ? Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.tax / 2,
                                              ).toFixed(2)
                                            : Number(0)?.toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Taxes (SGST)
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          Rs{" "}
                                          {clinicDet?.petShopTaxId?.length > 0
                                            ? Number(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.tax / 2,
                                              ).toFixed(2)
                                            : Number(0)?.toFixed(2)}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-row-ali-cen mv5">
                                      <div className="w60Per">
                                        <div className="txtstyle">
                                          Round Off
                                        </div>
                                      </div>
                                      <div className="w40Per flex-end">
                                        <div className="txtstyle">
                                          {`Rs ${Number(
                                            Number(
                                              roundWithSignedDiff(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.subTotal,
                                              )?.difference,
                                            ),
                                          )?.toFixed(2)}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="balancecontainer ph5 mt10">
                                      <div className="flex-row-ali-cen w100Per">
                                        <div className="w60Per">
                                          <div className="txt-semi-bold fs14 blue-color">
                                            Total
                                          </div>
                                        </div>
                                        <div className="w40Per flex-end">
                                          <div className="txt-semi-bold fs14 blue-color">
                                            {`Rs. ${Number(
                                              roundWithSignedDiff(
                                                getTaxAndSubTotalAndTotal(
                                                  getPetShopRecordBasedType(
                                                    selectedBill?.paymentDetails,
                                                  ),
                                                )?.subTotal,
                                              )?.rounded,
                                            )?.toFixed(2)}`}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          <div className="mb20" />
                        </tbody>
                      </table>
                      <div className="flex-row mv20 ph10">
                        <img
                          src={require("../../../../assets/images/jpg/WoofQrCode.jpeg")}
                          alt=""
                          className="img-h50"
                        />
                        <div className="blu-back wh50 flex-center">
                          <img
                            src={require("../../../../assets/images/png/VetInstantLogo.png")}
                            alt=""
                            className="img-h40"
                          />
                        </div>
                        <div className="flex-center">
                          <div className="font-bold fs14 ml20">
                            Scan to download our app
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          {getTreatmentRecordBasedType(selectedBill?.paymentDetails)?.length +
            getPharmacyRecordBasedType(selectedBill?.paymentDetails)?.length +
            getPetShopRecordBasedType(selectedBill?.paymentDetails)?.length >
          0 ? null : (
            <div className="no-rec">No records available</div>
          )}
          {getTreatmentRecordBasedType(selectedBill?.paymentDetails)?.length +
            getPharmacyRecordBasedType(selectedBill?.paymentDetails)?.length >
          0 ? (
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              className="back-white"
            >
              <div className="flex-end">
                <div className="mr20 mv20">
                  <CustomButton
                    text="Download"
                    onClick={() =>
                      handlePrint(null, () => contentToPrint.current)
                    }
                    smallBtn
                  />
                </div>
              </div>
            </Grid>
          ) : null}
        </div>
      </CustomModal>
      <CustomModal
        open={refereneValues?.visible}
        onClose={() => setRefereneValues({ visible: false, referenceNo: null })}
        header="Select Reference No"
        modal
        modalWidth={25}
      >
        <div className="ph20 pb20">
          <div className="font-medium fs14 black2">Reference No</div>
          <Select
            list={getReferenceNos()}
            value={refereneValues?.referenceNo}
            handleChange={(e, value) => {
              setRefereneValues({ ...refereneValues, referenceNo: value });
            }}
            search
          />
          <div className="flex1-end mt20">
            <div>
              <CustomButton
                text="Submit"
                onClick={() => handleCreateBill(true)}
                smallBtn
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default BillingHistory;
